import { useCallback, useRef, useState } from "react";
import * as Icons from "react-icons/bs";
import { TDocumentRel, TWindowPosition, Themes } from "../../types";
import useDraggable from "../../hooks/useDraggable";
import '../../scss/lex-doc-rel.scss';
import '../../scss/vintage-controls.scss';
import { useDocuments } from "../../contexts/DekstopContext";
import { Form } from "react-bootstrap";
import Checkbox from "../shared/Checkbox";
import RadioGroup from "../shared/RadioGroup";

const LimitModal = ({
  theme,
  type,
  onClose,
}: {
  theme: Themes,
  type: 'first' | 'last',
  onClose: () => void,
}) => {
  const [position, setPosition] = useState<TWindowPosition>({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const windowRef = useRef<HTMLDivElement>(null);

  const { isDragging, handleMouseDown } = useDraggable({
    initialPosition: position,
    containerRef,
    elementRef: windowRef,
    onPositionChange: (p) => setPosition(p),
    autoAlignToMiddle: true,
  });

  return (
    <div className="full-screen-modal" ref={containerRef}>
      <div
        className={`virtual-window advanced-search-container  ${isDragging ? "dragging" : ""} ${theme}`}
        ref={windowRef}
        style={{
          position: "absolute",
          top: `${position.y}px`,
          left: `${position.x}px`,
          height: '8.5rem',
          width: '25rem'
        }}
      >
        <div className="window-header" onMouseDown={handleMouseDown}>
          <div className="window-title">
            <i className="file-icon">
              <Icons.BsBinocularsFill />
            </i>
            <span>Cauta</span>
          </div>
          <div className="window-controls">
            <button
              className="window-control close"
              onClick={onClose}
            >
              ✕
            </button>
          </div>
        </div>
        <div className="window-content d-flex flex-row align-items-center w-100 justify-content-center" style={{ gap: '0.5rem' }}>
          <Icons.BsExclamationTriangleFill />
          {
            type === 'first'
              ? "Am ajuns la inceputul textului"
              : null
          }
          {
            type === 'last'
              ? "Am ajuns la sfarsitul textului"
              : null
          }

        </div>
        <div className="window-footer" style={{ justifyContent: 'center' }}>
          {/* <div className="advanced-search-button" style={{ opacity: 0 }}></div> */}
          <button className="advanced-search-button" style={{ marginLeft: 'auto', marginRight: 'unset' }} onClick={onClose}>
            OK
          </button>
        </div>

      </div>
    </div>
  )
}

const InDocumentSearchModal = ({
  theme,
  onClose,
}: {
  theme: Themes,
  onClose: () => void,
}) => {
  const [position, setPosition] = useState<TWindowPosition>({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const windowRef = useRef<HTMLDivElement>(null);

  const [searchText, setSearchText] = useState<string>();
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [searchBackwards, setSearchBackwards] = useState(false);

  const [reachedLimit, setReachedLimit] = useState<'first' | 'last'>();
  const [showLimitModal, setShowLimitModal] = useState(false);

  const { onInnerDocumentSearch, unsetInnerDocumentSearch } = useDocuments();

  const { isDragging, handleMouseDown } = useDraggable({
    initialPosition: position,
    containerRef,
    elementRef: windowRef,
    onPositionChange: (p) => setPosition(p),
    autoAlignToMiddle: true,
  });

  const handleNext = useCallback(() => {
    if (!searchText) return;
    if (reachedLimit === 'last' && searchBackwards === false) {
      setShowLimitModal(true);
      return;
    }
    if (reachedLimit === 'first' && searchBackwards === true) {
      setShowLimitModal(true);
      return;
    }
    const result = onInnerDocumentSearch(searchText, caseSensitive, searchBackwards);
    if (result === 'first') {
      setReachedLimit('first');
    } else if (result === 'last') {
      setReachedLimit('last');
    } else {
      setReachedLimit(undefined);
    }

  }, [caseSensitive, onInnerDocumentSearch, reachedLimit, searchBackwards, searchText])

  return (
    <>
      <div className="full-screen-modal" ref={containerRef}>
        <div
          className={`virtual-window advanced-search-container  ${isDragging ? "dragging" : ""} ${theme}`}
          ref={windowRef}
          style={{
            position: "absolute",
            top: `${position.y}px`,
            left: `${position.x}px`,
            height: '8.5rem',
            width: '25rem'
          }}
        >
          <div className="window-header" onMouseDown={handleMouseDown}>
            <div className="window-title">
              <i className="file-icon">
                <Icons.BsBinocularsFill />
              </i>
              <span>Cauta</span>
            </div>
            <div className="window-controls">
              <button
                className="window-control close"
                onClick={() => {
                  onInnerDocumentSearch("");
                  onClose()
                }}
              >
                ✕
              </button>
            </div>
          </div>
          <div className="window-content">
            <div className='d-flex flex-row align-items-start justify-content-between' style={{ gap: '1rem', width: '24rem' }}>
              <div className='d-flex flex-column' style={{ width: 'calc(100% - 5rem)' }}>
                <div className='d-flex flex-row align-items-center'>
                  <div style={{ width: '5rem' }}>
                    De cautat
                  </div>
                  <Form.Control
                    className="form-control px-1"
                    type="string"
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                      // setReachedLimit(prev => );
                    }}
                    onKeyDown={(e) => {
                      if (e.code === 'Enter') {
                        if (searchText) {
                          const result = onInnerDocumentSearch(searchText, caseSensitive);
                          setReachedLimit(result as 'first' | 'last' | undefined);
                        } else {
                          unsetInnerDocumentSearch();
                        }
                      }
                    }}
                  />
                </div>
                <div className='d-flex flex-row align-items-end justify-content-between'>
                  <div className='d-flex flex-row align-items-center justify-content-between' style={{ gap: '0.5rem' }}>
                    <Checkbox
                      checked={caseSensitive}
                      label={<>ABC <Icons.BsChevronExpand style={{ rotate: '90deg' }} /> abc</>}
                      onChange={(value => setCaseSensitive(value))}
                    />
                  </div>
                  <RadioGroup
                    label="Directie"
                    value={searchBackwards ? 'Inapoi' : 'Inainte'}
                    options={["Inapoi", "Inainte"]}
                    onChange={(e) => setSearchBackwards(e === 'Inapoi')}
                  />
                </div>
              </div>
              <div className='d-flex flex-column ml-auto' style={{ width: '5rem' }}>
                <button
                  className={`advanced-search-button`}
                  style={{ marginRight: 'unset' }}
                  onClick={handleNext}
                >
                  Urmatorul
                </button>
                <button
                  className="advanced-search-button"
                  style={{ marginLeft: 'unset' }}
                  onClick={() => {
                    unsetInnerDocumentSearch();
                    onClose()
                  }}
                >
                  Renunta
                </button>
              </div>
            </div>

          </div>

        </div>
      </div>
      {
        showLimitModal && reachedLimit
          ? <LimitModal
            theme={theme}
            type={reachedLimit}
            onClose={() => setShowLimitModal(false)}
          />
          : null
      }
    </>
  )
}

export default InDocumentSearchModal;
